import { Button, Stack, TextField, Typography } from "@mui/material";
import { useSimulatePaymentSuccessMutation } from "Api/Mutations/Transactions/useSimulatePaymentSuccessMutation";
import { ConfirmDialog } from "Components/Shared/Dialogs/ConfirmDialog";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useConfirmDialog } from "Hooks/useConfirmDialog";
import { useCheckPermission } from "Hooks/usePermission";
import { Permission } from "Infrastructure/Api/Api";
import { hasApiProblemError } from "Infrastructure/Api/ApiUtils";
import { PAYMENT_SIMULATION_CONFIG } from "Infrastructure/Utils/PaymentSimulationUtils";
import { AccessRightCodes } from "Models/AccessRightCodes";
import { useState } from "react";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Admin.PaymentSimulation;

// The backend keys each refusal by the Result.Fail subject, so the key says which gate refused.
const ERROR_RESOURCES_BY_KEY = {
  PublicID: RESOURCES.Errors.NotFound,
  User: RESOURCES.Errors.Forbidden,
  Type: RESOURCES.Errors.NotPayment,
  Payment: RESOURCES.Errors.NotSandbox,
  Status: RESOURCES.Errors.StatusNotAllowed,
};

export const PaymentSimulationContent = () => {
  const { t } = useResource();
  const [publicID, setPublicID] = useState("");
  const canSimulate = useCheckPermission(
    AccessRightCodes.SystemControl,
    Permission.Update,
  );
  const { isOpen, open, handleResult } = useConfirmDialog();
  const { mutate: simulate, isPending } = useSimulatePaymentSuccessMutation();

  if (!PAYMENT_SIMULATION_CONFIG.IS_ENABLED || !canSimulate) {
    return null;
  }

  const trimmedPublicID = publicID.trim();

  const handleSimulate = () => {
    simulate(trimmedPublicID, {
      onSuccess: (result) => {
        toast.success(t(RESOURCES.Success, { publicID: result.publicID }));
        setPublicID("");
      },
      onError: (error) => {
        const matched = Object.entries(ERROR_RESOURCES_BY_KEY).find(([key]) =>
          hasApiProblemError(error, key),
        );
        toast.error(t(matched?.[1] ?? RESOURCES.Errors.Generic));
      },
    });
  };

  return (
    <PrimaryPaper>
      <Stack gap={2} alignItems="flex-start">
        <Typography variant="h6">{t(RESOURCES.Title)}</Typography>
        <Typography>{t(RESOURCES.Description)}</Typography>
        <TextField
          label={t(RESOURCES.PublicIDLabel)}
          value={publicID}
          onChange={(event) => setPublicID(event.target.value)}
          size="small"
        />
        <Button
          variant="contained"
          onClick={open}
          disabled={trimmedPublicID === "" || isPending}
        >
          {isPending ? t(RESOURCES.Loading) : t(RESOURCES.Button)}
        </Button>
      </Stack>
      <ConfirmDialog
        isOpen={isOpen}
        handleResult={handleResult(handleSimulate)}
        modalTitle={t(RESOURCES.ConfirmTitle)}
        modalContent={
          <Typography>
            {t(RESOURCES.ConfirmText, { publicID: trimmedPublicID })}
          </Typography>
        }
        confirmButtonText={t(RESOURCES.Button)}
      />
    </PrimaryPaper>
  );
};
